import { Card, Divider, Stack, Switch, Table, Typography, styled } from '@healthinal/ui';
import { compareDesc, endOfYesterday, isAfter } from 'date-fns';
import DOMPurify from 'dompurify';
import { HTMLProps, useState } from 'react';
import { HeurekaCondition } from '../api/heurekaPatientDetails';
import { theme } from '../theme';
import { formatDate } from '../utils/date';
import { TabularNumbers } from './TabularNumbers';

interface Props {
  title: string;
  conditions: HeurekaCondition[];
}

export default function ConditionTable({ conditions, title }: Props) {
  const [showInactiveCondition, setShowInactiveCondition] = useState(false);

  const filteredAndSortedConditions = conditions
    .filter((condition) => isConditionActive(condition) || showInactiveCondition)
    .sort(sortConditions);

  const isInactiveConditionAvailable = conditions.some((m) => !isConditionActive(m));

  const showCodings = conditions.some((condition) => condition.codings.length > 0);
  const showDuration = conditions.some((condition) => condition.from || condition.until);

  return (
    <StyledCard>
      <CardHeader>
        <Typography level="title-md" fontWeight={600}>
          {title}
        </Typography>
        {isInactiveConditionAvailable && (
          <>
            <Divider orientation="vertical" />
            <Typography
              component="label"
              level="body-sm"
              textColor="text.secondary"
              startDecorator={
                <Switch
                  checked={showInactiveCondition}
                  onChange={(e) => setShowInactiveCondition(e.target.checked)}
                  sx={{ mr: 1 }}
                />
              }>
              Frühere Diagnosen anzeigen
            </Typography>
          </>
        )}
      </CardHeader>
      <CardContent>
        <StyledTable>
          <thead>
            <tr>
              {showCodings && (
                <>
                  <th style={{ width: 190 }}>Klassifikation & Code</th>
                  <th style={{ width: 200 }}>Bezeichnung</th>
                </>
              )}

              <th style={{ width: 400 }}>Bemerkung</th>
              <th style={{ width: 120 }}>Erfasst</th>

              {showDuration && <th style={{ width: 232 }}>Dauer</th>}
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedConditions.map((condition, index) => {
              return (
                <ConditionRow key={index} condition={condition} showCodings={showCodings} showDuration={showDuration} />
              );
            })}
          </tbody>
        </StyledTable>
      </CardContent>
    </StyledCard>
  );
}

function activeConditionFirst(a: HeurekaCondition, b: HeurekaCondition) {
  const aActive = isConditionActive(a);
  const bActive = isConditionActive(b);
  if (aActive === bActive) return 0;
  return aActive ? -1 : 1;
}

function dateDescending(a?: Date, b?: Date, prioritizeUndefinedDate = false) {
  if (!b && !a) return 0;
  if (a && b) return compareDesc(a, b);
  else if (prioritizeUndefinedDate) return a ? 1 : -1;
  else return a ? -1 : 1;
}

function nameAscending(a: HeurekaCondition, b: HeurekaCondition) {
  const aName = a.codings?.[0]?.display ?? '';
  const bName = b.codings?.[0]?.display ?? '';
  return aName.localeCompare(bName);
}

function sortConditions(a: HeurekaCondition, b: HeurekaCondition) {
  return (
    activeConditionFirst(a, b) ||
    dateDescending(a.from, b.from) ||
    dateDescending(a.until, b.until, true) ||
    nameAscending(a, b)
  );
}

const isConditionActive = (condition: HeurekaCondition) =>
  // endOfYesterday() so condition ending today is still considered active
  !condition.until || isAfter(condition.until, endOfYesterday());

const StyledCard = styled(Card)`
  --Card-padding: 0;
  overflow: hidden; // for rounded corners
  margin-top: 30px;
  margin-bottom: 40px;
`;

const CardHeader = styled('div')`
  padding: 24px 32px;
  border-bottom: solid 1px ${theme.vars.palette.divider};
  display: flex;
  gap: 24px;
`;

const CardContent = styled('div')`
  padding: 0 16px 16px;
`;

const StyledTable = styled(Table)`
  --Table-headerUnderlineThickness: 1px;
  --TableCell-paddingX: 16px;
  --TableCell-paddingY: 9px;

  thead th {
    ${theme.typography['title-sm']};
    vertical-align: middle;
  }

  td {
    vertical-align: top;
  }

  td,
  div,
  p {
    margin: 0;
  }

  td table td {
    border: none !important;
    padding: 0 !important;
    margin: 0 !important;
  }

  td,
  th {
    &:not(:first-of-type) {
      border-left: solid 1px var(--TableCell-borderColor);
    }
  }
`;

const CodingList = styled('ul')`
  margin: 0;
  padding-left: 16px;

  /* Hide bullets when there's only one item */
  &:has(li:only-child) {
    list-style: none;
    padding-left: 0;
  }

  li {
    padding: 0;
  }
`;

interface ConditionRowProps extends HTMLProps<HTMLTableRowElement> {
  condition: HeurekaCondition;
  showCodings: boolean;
  showDuration: boolean;
}

const ConditionRow = ({ condition, showCodings, showDuration, ...trProps }: ConditionRowProps) => (
  <tr {...trProps}>
    {showCodings && (
      <>
        <td>
          <CodingList>
            {condition.codings?.map((coding, index) => (
              <li key={index}>
                <Stack direction="row" alignItems="flex-end" gap={1}>
                  {coding.system}
                  <Typography textColor={'neutral.400'}>•</Typography>
                  {coding.code}
                </Stack>
              </li>
            ))}
          </CodingList>
        </td>
        <td>
          <CodingList>
            {condition.codings?.map((coding, index) => (
              <li key={index}>
                <TabularNumbers>{coding.display}</TabularNumbers>
              </li>
            ))}
          </CodingList>
        </td>
      </>
    )}

    <td>
      <RenderedHtmlElement
        style={{ tabSize: 2 }}
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(condition.comments.join('<br />'))
            // Epaad can contain a lot of irritating newlines and tabs
            .replace(/&nbsp;/g, ' ')
            .replace(/\n\s*\n/g, '\n')
            .replace(/^\n/g, ''),
        }}
      />
    </td>
    <td>{condition.recorded && <TabularNumbers>{formatDate(condition.recorded)}</TabularNumbers>}</td>

    {showDuration && (
      <td>
        {(condition.from || condition.until) && (
          <Stack direction="row" alignItems="flex-end" gap={1}>
            <TabularNumbers>{formatDate(condition.from)}</TabularNumbers>
            <Typography fontSize={12} textColor={'text.tertiary'}>
              bis
            </Typography>
            <TabularNumbers>{formatDate(condition.until)}</TabularNumbers>
          </Stack>
        )}
      </td>
    )}
  </tr>
);

const RenderedHtmlElement = styled('div')({
  'div.item-content': {
    display: 'flex',
    gap: '5px',
  },
  'div.item-text': {
    flex: '1',
  },
  'div.item-metadata': {
    flex: '0 auto',
  },
  whiteSpace: 'pre-wrap',
  p: {
    margin: 0,
  },
  ul: {
    paddingLeft: '20px',
  },
});
